import type { CancelablePromise, ScoreDistributionBin } from "@/client"
import { OpenAPI } from "@/client"
import { request as __request } from "@/client/core/request"

export interface QuestionAnalyticsItem {
  question_id: string
  question_text: string
  correctness_rate: number
  avg_time_seconds: number
  subject_names: string[]
  topic_names: string[]
  bookmark_count?: number
  note_count?: number
}

export interface QuestionAnalyticsResponse {
  questions: QuestionAnalyticsItem[]
  total_questions: number
}

export interface SubjectPerformanceItem {
  subject_id: string
  subject_name: string
  total_questions: number
  correctness_rate: number
  avg_time_seconds: number
}

export interface SubjectPerformanceResponse {
  subjects: SubjectPerformanceItem[]
  total_subjects: number
}

export interface TopicPerformanceItem {
  topic_id: string
  topic_name: string
  total_questions: number
  correctness_rate: number
  avg_time_seconds: number
}

export interface TopicPerformanceResponse {
  topics: TopicPerformanceItem[]
  total_topics: number
}

export interface TimeDistributionBucket {
  range_label: string
  min_seconds: number
  max_seconds: number
  count: number
}

export interface TimeDistributionResponse {
  buckets: TimeDistributionBucket[]
  avg_completion_seconds: number
  median_completion_seconds: number
}

export interface AnalyticsQueryParams {
  startDate?: string
  endDate?: string
}

const buildQuery = ({ startDate, endDate }: AnalyticsQueryParams = {}) => {
  const query: Record<string, string | undefined> = {}

  if (startDate) {
    query.start_date = startDate
  }

  if (endDate) {
    query.end_date = endDate
  }

  return query
}

export const getQuestionAnalytics = (
  quizId: string,
  params?: AnalyticsQueryParams,
): CancelablePromise<QuestionAnalyticsResponse> => {
  return __request(OpenAPI, {
    method: "GET",
    url: "/api/v1/quizzes/{quiz_id}/analytics/questions",
    path: {
      quiz_id: quizId,
    },
    query: buildQuery(params),
  })
}

export const getSubjectPerformance = (
  quizId: string,
  params?: AnalyticsQueryParams,
): CancelablePromise<SubjectPerformanceResponse> => {
  return __request(OpenAPI, {
    method: "GET",
    url: "/api/v1/quizzes/{quiz_id}/analytics/subjects",
    path: {
      quiz_id: quizId,
    },
    query: buildQuery(params),
  })
}

export const getTopicPerformance = (
  quizId: string,
  params?: AnalyticsQueryParams,
): CancelablePromise<TopicPerformanceResponse> => {
  return __request(OpenAPI, {
    method: "GET",
    url: "/api/v1/quizzes/{quiz_id}/analytics/topics",
    path: {
      quiz_id: quizId,
    },
    query: buildQuery(params),
  })
}

export const getTimeDistribution = (
  quizId: string,
  params?: AnalyticsQueryParams,
): CancelablePromise<TimeDistributionResponse> => {
  return __request(OpenAPI, {
    method: "GET",
    url: "/api/v1/quizzes/{quiz_id}/analytics/time-distribution",
    path: {
      quiz_id: quizId,
    },
    query: buildQuery(params),
  })
}

export interface AnswerChoice {
  option_index: number
  option_text: string
  is_correct: boolean
  selection_percentage: number
}

export interface QuestionAnswerDistribution {
  question_id: string
  question_text: string
  answer_choices: AnswerChoice[]
}

export interface QuizAnswerDistributionResponse {
  questions: QuestionAnswerDistribution[]
  total_questions: number
}

export const getAnswerDistribution = (
  quizId: string,
  params?: AnalyticsQueryParams,
): CancelablePromise<QuizAnswerDistributionResponse> => {
  return __request(OpenAPI, {
    method: "GET",
    url: "/api/v1/quizzes/{quiz_id}/analytics/answer-distribution",
    path: {
      quiz_id: quizId,
    },
    query: buildQuery(params),
  })
}

export interface CohortOverallDistribution {
  max_score: number
  bins: ScoreDistributionBin[]
}

export interface CohortSubjectDistribution {
  subject_id: string
  subject_name: string
  question_count: number
  max_score: number
  bins: ScoreDistributionBin[]
}

export interface CohortDistributionResponse {
  quiz_id: string
  is_ready: boolean
  overall: CohortOverallDistribution | null
  subjects: CohortSubjectDistribution[]
}

export const getCohortDistribution = (
  quizId: string,
): CancelablePromise<CohortDistributionResponse> => {
  return __request(OpenAPI, {
    method: "GET",
    url: "/api/v1/quizzes/{quiz_id}/analytics/cohort-distribution",
    path: {
      quiz_id: quizId,
    },
  })
}
